import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/pages/admin/AdminDashboard";
import { toast } from "sonner";
import { PlusCircle, Trash2 } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

function diffDays(from, to) {
  if (!from || !to) return 0;
  const f = new Date(from);
  const t = new Date(to);
  const ms = t - f;
  if (ms < 0) return 0;
  return Math.floor(ms / (24 * 3600 * 1000)) + 1;
}

const emptyForm = { from_date: todayISO(), to_date: todayISO(), reason: "" };

export default function MyLeaves() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = () => api.get("/leaves").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.reason.trim()) { toast.error("Reason is required"); return; }
    if (form.to_date < form.from_date) { toast.error("End date cannot be before start date"); return; }
    setSaving(true);
    try {
      await api.post("/leaves", form);
      toast.success("Leave applied");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    try {
      await api.delete(`/leaves/${cancelTarget.id}`);
      toast.success("Leave cancelled");
      setCancelTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const days = diffDays(form.from_date, form.to_date);

  return (
    <Shell
      title="My Leaves"
      subtitle="Apply for leave and track approval status."
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="apply-leave-button">
              <PlusCircle size={16} className="mr-1" /> Apply Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" data-testid="apply-leave-dialog">
            <DialogHeader><DialogTitle>Apply for Leave</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold">From</Label>
                  <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} data-testid="leave-from-date" />
                </div>
                <div>
                  <Label className="font-semibold">To</Label>
                  <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} data-testid="leave-to-date" />
                </div>
              </div>
              <div className="p-3 border border-zinc-200 rounded-md bg-zinc-50 flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">No. of days</div>
                <div className="text-xl font-extrabold tracking-tighter font-heading tabular-nums" data-testid="leave-days-count">{days}</div>
              </div>
              <div>
                <Label className="font-semibold">Reason</Label>
                <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Briefly explain the reason" data-testid="leave-reason" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="leave-submit-button">
                {saving ? "Submitting…" : "Submit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <Card className="border-zinc-200 shadow-none rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="font-bold text-zinc-950">From</TableHead>
              <TableHead className="font-bold text-zinc-950">To</TableHead>
              <TableHead className="font-bold text-zinc-950 text-right">Days</TableHead>
              <TableHead className="font-bold text-zinc-950">Reason</TableHead>
              <TableHead className="font-bold text-zinc-950 text-right">Status</TableHead>
              <TableHead className="font-bold text-zinc-950 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-zinc-500">No leave requests yet.</TableCell></TableRow>
            )}
            {items.map((lv) => (
              <TableRow key={lv.id} data-testid={`my-leave-${lv.id}`}>
                <TableCell>{lv.from_date}</TableCell>
                <TableCell>{lv.to_date}</TableCell>
                <TableCell className="text-right tabular-nums">{lv.total_days}</TableCell>
                <TableCell className="max-w-xs truncate" title={lv.reason}>{lv.reason}</TableCell>
                <TableCell className="text-right">
                  <div className="space-y-1">
                    <StatusBadge status={lv.status} />
                    {lv.rejection_reason && <div className="text-xs text-rose-700 italic">{lv.rejection_reason}</div>}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {lv.status === "pending" && (
                    <Button variant="ghost" size="sm" onClick={() => setCancelTarget(lv)} data-testid={`cancel-leave-${lv.id}`}>
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent data-testid="cancel-leave-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel leave application?</AlertDialogTitle>
            <AlertDialogDescription>
              This will withdraw your leave request ({cancelTarget?.from_date} → {cancelTarget?.to_date}). You can re-apply anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-leave-back">Keep request</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="cancel-leave-confirm">
              Yes, cancel it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
